import { z } from 'zod'
import { Flow, StepType } from './steps.js'
import { Container, Capabilities } from './container.js'
import { Theme } from './theme.js'

export const SessionStatus = z.enum(['pending', 'in_progress', 'signed', 'expired', 'failed'])
export type SessionStatus = z.infer<typeof SessionStatus>

export const PartnerProfile = z.enum(['bank', 'non_bank'])
export type PartnerProfile = z.infer<typeof PartnerProfile>

/**
 * SessionContext — contract BE↔FE duy nhất.
 *
 * Toàn bộ FE render từ object này. Nếu một màn hình cần biết thứ gì đó mà object này không có,
 * thì thiếu field ở đây — KHÔNG phải thiếu một nhánh `if` trong component.
 *
 * Object này là SNAPSHOT lúc tạo session, không đọc live từ partner config.
 * Partner đổi màu / bớt step khi 200 người đang ký dở thì stepper nhảy số giữa chừng.
 */
export const SessionContext = z.object({
  session: z.object({
    id: z.string(),
    expires_at: z.string().datetime(),
    current_step: StepType,
    completed_steps: z.array(StepType),
    status: SessionStatus,
  }),

  partner: z.object({
    id: z.string(),
    name: z.string(),
    profile: PartnerProfile,
  }),

  container: Container,
  capabilities: Capabilities,
  flow: Flow,
  theme: Theme,

  legal: z.object({
    template_id: z.string(),
    template_version: z.number().int().positive(),
  }),

  /** Dữ liệu prefill. Đi qua kênh server-to-server ②, KHÔNG bao giờ nằm trong JWT hay URL. */
  subject: z.record(z.string(), z.union([z.string(), z.array(z.string())])).default({}),

  i18n: z.object({ locale: z.literal('vi') }),
})
export type SessionContext = z.infer<typeof SessionContext>

/**
 * Kết quả submit một step. BE luôn trả `next_step` — FE không tự tính bước kế tiếp.
 * `error_code` là cái quyết định nhánh nghiệp vụ (ví dụ 3 nhánh của `link_account`),
 * không phải FE đoán từ shape dữ liệu.
 */
export const StepResult = z.object({
  ok: z.boolean(),
  next_step: StepType.nullable(),
  status: SessionStatus,
  error_code: z.string().nullable().default(null),
  data: z.record(z.string(), z.unknown()).default({}),
})
export type StepResult = z.infer<typeof StepResult>

export const ApiError = z.object({
  error_code: z.string(),
  message: z.string(),
  session_id: z.string().nullable().default(null),
})
export type ApiError = z.infer<typeof ApiError>
